import { useEffect, useRef, useState } from 'react';
import { resolveMedia, templateById } from '../data/videoTemplates';
import { useSound } from '../utils/soundBus';
import { Modal } from '../components/Modal';
import { SoundButton } from '../components/SoundButton';
import { Icon } from '../components/Icon';
import { UploadCta } from '../components/UploadCta';
import './TemplatePreviewPopup.css';

const DISCLAIMERS = [
    'Photo upload for generation is not supported',
    'You are solely responsible for the images you create.',
    'This website is designed for entertainment purposes only.',
];

const STEPS = ['Add image', 'Generate', 'Get your video'];

function PreviewBody({ template, soon, startWithSound, onGenerate, onOpenTemplate, onBrowseLive }) {
    const media = resolveMedia(template);
    const sound = useSound(`popup:${template.id}`);
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const fallback = soon ? templateById(template.fallback) : null;

    // Carry the sound over when the user already had it on for this card.
    useEffect(() => { if (startWithSound && template.audio) sound.claim(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !sound.on;
        if (v.paused) v.play().catch(() => {});
    }, [sound.on]);

    return (
        <div className="tplPop">
            <figure className="tplPopMedia" style={media.blur ? { backgroundImage: `url(${media.blur})` } : undefined}>
                <img src={media.poster} alt="" />
                <video
                    ref={videoRef}
                    className={playing ? 'isPlaying' : ''}
                    src={media.video}
                    autoPlay loop playsInline
                    muted={!sound.on}
                    preload="auto"
                    onPlaying={() => setPlaying(true)}
                />
                <span className="tplPopScrim" aria-hidden="true" />
                {/* Phones: the heading sits on the clip */}
                <figcaption className="tplPopOnMedia">
                    {soon && <span className="tplPopRibbon">Coming soon</span>}
                    <span className="tplPopTitle">{template.title}</span>
                    <span className="tplPopSub">{soon ? 'A new scene, landing soon' : 'Customize persona in this video'}</span>
                </figcaption>
                {template.audio && <SoundButton on={sound.on} onToggle={sound.toggle} size="lg" className="tplPopSound" />}
            </figure>

            <div className="tplPopSide">
                <div className="tplPopHead">
                    <span className="eyebrow"><span className="dot" /> {soon ? 'Coming soon' : 'AI video scene'}</span>
                    <h2 className="tplPopTitle">{template.title}</h2>
                    <p className="tplPopSub">{soon ? 'A new scene, landing soon' : 'Customize persona in this video'}</p>
                </div>

                {soon ? (
                    <div className="tplPopSoon">
                        <p className="tplPopSoonText">
                            <span className="dot dot--pulse" /> We&apos;re finishing this scene. Meanwhile, the closest one is ready right now.
                        </p>
                        {fallback && (
                            <button type="button" className="btn btn--primary btn--lg btn--block" onClick={() => onOpenTemplate(fallback)}>
                                Try {fallback.title} now <Icon name="arrowRight" size={16} strokeWidth={2.4} />
                            </button>
                        )}
                        <button type="button" className="btn btn--glass btn--block" onClick={onBrowseLive}>Browse available</button>
                    </div>
                ) : (
                    <>
                        <ol className="tplPopSteps">
                            {STEPS.map((s) => <li key={s}>{s}</li>)}
                        </ol>
                        <div className="tplPopAction">
                            <UploadCta label="Add image" onClick={onGenerate} />
                            <p className="tplPopCaption">A sample or one of your photos · video in ~2 min</p>
                        </div>
                        <ol className="tplPopNotes">
                            {DISCLAIMERS.map((d) => <li key={d}>{d}</li>)}
                        </ol>
                    </>
                )}
            </div>
        </div>
    );
}

// `paid`: visitors without a subscription get the same view for every scene; the soon state is subscriber-only.
export function TemplatePreviewPopup({ template, paid, startWithSound, onClose, onGenerate, onOpenTemplate, onBrowseLive }) {
    const soon = paid && template?.status === 'soon';

    return (
        <Modal isOpen={!!template} onClose={onClose} size="lg" label={template?.title} className="tplModal">
            {template && (
                <PreviewBody
                    key={template.id}
                    template={template}
                    soon={soon}
                    startWithSound={startWithSound}
                    onGenerate={onGenerate}
                    onOpenTemplate={onOpenTemplate}
                    onBrowseLive={onBrowseLive}
                />
            )}
        </Modal>
    );
}
